"use client";

import { usePathname } from "next/navigation";
import type { ReactNode } from "react";

import { PAGES_WITH_UNSTICKY_HEADER_IN_MOBILE } from "../../../../constants";

/*
 * 部屋タイプ予約ブラウズページのレスポンシブレイアウトシェル。
 *
 * SP（<1024px）:
 *   - 上部: SP 検索バー（spSearchBar スロット）= 日程/エリア/絞り込みアイコンの横並び h-[54px]
 *   - サイドバー（aside）は hidden（PC 専用）
 *   - メインが全幅で縦1カラム表示
 * PC（≥1024px）: 左サイドバー（1024–1439:320px / 1440+:400px）＋右メインエリア（flex-1）の2カラム。
 *   横padding は付けない。コンテンツ余白は内側wrapperの lg:p-[40px] に集約。
 * 1440+: main 内に2段ネストで幅を制限:
 *   外側div: 残りの90%・左寄せ（mx-auto なし）。
 *   内側div: 外側の90%・中央寄せ（mx-auto）・max1080px。
 *   → 内容幅 = min((vw−400)×0.81, 1080)。
 *   1600→972 / 1440→842 / cap1080 は vw≈1733〜。
 */
export function BrowseLayout({
  filter,
  main,
  spSearchBar,
}: {
  filter: ReactNode;
  main: ReactNode;
  /** SP（<1024px）上部に表示する検索バー。PC では非表示になる。 */
  spSearchBar?: ReactNode;
}) {
  const pathname = usePathname();
  const isHeaderUnstickyInMobile = PAGES_WITH_UNSTICKY_HEADER_IN_MOBILE.some(
    (hiddenPath) =>
      pathname === hiddenPath || pathname.startsWith(`${hiddenPath}/`),
  );

  return (
    // overflow-anchor-none: 初回ロード時、一覧が skeleton から実データへ差し替わる瞬間に
    // sticky な spSearchBar が scroll anchoring の anchor 候補に誤選択され、
    // ブラウザがスクロール位置を spSearchBar の位置へ自動補正してしまう問題の回避
    <div className="bg-dx-surface-base flex flex-col [overflow-anchor:none]">
      {/* グローバルヘッダー（(default) layout の sticky top-0, h-global-header-*）の
          直下に固定する。top-0 だとヘッダーと重なるため高さ分オフセットする。
          対象ページはヘッダーの sticky が md 未満のみ解除されるため、md 未満はオフセットせず top-0 にする
          （md 以上はヘッダーの sticky が復帰するため、通常通り高さ分オフセットする）。 */}
      {spSearchBar && (
        <div
          className={
            isHeaderUnstickyInMobile
              ? "bg-dx-surface-base md:top-global-header-md sticky top-0 z-30 lg:hidden"
              : "bg-dx-surface-base top-global-header-min md:top-global-header-md sticky z-30 lg:hidden"
          }
        >
          {spSearchBar}
        </div>
      )}

      {/* items-start は sidebar の sticky 動作に必須 */}
      <div className="lg:flex lg:items-start">
        {/* lg+ はフィルタを上端固定（親の items-start が前提）。グローバルヘッダー高さ分オフセット。 */}
        <aside
          aria-label="絞り込みフィルター"
          className="lg:top-global-header-md hidden w-full shrink-0 min-[1440px]:w-[400px] lg:sticky lg:block lg:w-[320px] lg:self-start"
        >
          {filter}
        </aside>

        <main
          aria-label="部屋タイプ一覧"
          className="bg-dx-surface-base w-full lg:min-w-0 lg:flex-1"
        >
          <div className="w-full min-[1440px]:w-[90%]">
            <div className="mx-auto w-full max-w-[1080px] min-[1440px]:w-[90%] lg:p-[40px]">
              {main}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
